"use client";

import { useCallback, useEffect, useRef, useState } from "react";

interface UseSplitPositionArgs {
    defaultPercent?: number;
    minPercent?: number;
    maxPercent?: number;
    storageKey?: string;
}

export function useSplitPosition({
    defaultPercent = 50,
    minPercent = 20,
    maxPercent = 80,
    storageKey,
}: UseSplitPositionArgs = {}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const draggingRef = useRef(false);
    const percentRef = useRef(defaultPercent);
    const [percent, setPercent] = useState<number>(defaultPercent);
    const [isDesktop, setIsDesktop] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        percentRef.current = percent;
    }, [percent]);

    // Restore a persisted split ratio, once, on mount
    useEffect(() => {
        if (!storageKey) return;
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                const n = parseFloat(stored);
                if (!isNaN(n)) setPercent(Math.min(maxPercent, Math.max(minPercent, n)));
            }
        } catch {
            // ignore - localStorage unavailable
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageKey]);

    useEffect(() => {
        const mq = window.matchMedia("(min-width: 768px)");
        const update = () => setIsDesktop(mq.matches);
        update();
        mq.addEventListener("change", update);
        return () => mq.removeEventListener("change", update);
    }, []);

    const updateFromClientX = useCallback(
        (clientX: number) => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            let pct = ((clientX - rect.left) / rect.width) * 100;
            pct = Math.min(maxPercent, Math.max(minPercent, pct));
            setPercent(pct);
        },
        [minPercent, maxPercent]
    );

    useEffect(() => {
        const onMouseMove = (e: MouseEvent) => {
            if (draggingRef.current) updateFromClientX(e.clientX);
        };
        const onTouchMove = (e: TouchEvent) => {
            if (draggingRef.current && e.touches[0]) {
                updateFromClientX(e.touches[0].clientX);
            }
        };
        const stopDrag = () => {
            if (!draggingRef.current) return;
            draggingRef.current = false;
            setIsDragging(false);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
            if (storageKey) {
                try {
                    localStorage.setItem(storageKey, String(percentRef.current));
                } catch {
                    // ignore
                }
            }
        };
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("touchmove", onTouchMove);
        window.addEventListener("mouseup", stopDrag);
        window.addEventListener("touchend", stopDrag);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("touchmove", onTouchMove);
            window.removeEventListener("mouseup", stopDrag);
            window.removeEventListener("touchend", stopDrag);
        };
    }, [updateFromClientX, storageKey]);

    const startDrag = useCallback(() => {
        draggingRef.current = true;
        setIsDragging(true);
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
    }, []);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            let delta = 0;
            if (e.key === "ArrowLeft") delta = -2;
            else if (e.key === "ArrowRight") delta = 2;
            else if (e.key === "Home") {
                setPercent(minPercent);
                return;
            } else if (e.key === "End") {
                setPercent(maxPercent);
                return;
            } else return;
            e.preventDefault();
            setPercent((p) => Math.min(maxPercent, Math.max(minPercent, p + delta)));
        },
        [minPercent, maxPercent]
    );

    const resetToDefault = useCallback(() => setPercent(defaultPercent), [defaultPercent]);

    return { containerRef, percent, isDesktop, isDragging, startDrag, handleKeyDown, resetToDefault };
}
